// this is the web polyfill
import 'barcode-detector';
import {BarcodeFormat, BarcodeScanner, LensFacing} from '@capacitor-mlkit/barcode-scanning';
import {EExternalProjectType} from '../Data/Data';
import {CordovaService} from './CordovaService';
import {StartScanOptions} from '@capacitor-mlkit/barcode-scanning/dist/esm/definitions';

export enum CameraDirection {
  Back = 0,
  Front = 1,
}

export interface ScanResult {
  text: string;
  format: string;
  cancelled: boolean;
}

export interface IScannerConfig {
  preferFrontCamera?: boolean;
  showFlipCameraButton?: boolean;
  disableAnimations?: boolean;
  disableSuccessBeep?: boolean;
  showTorchButton?: boolean;
}

export class ScannerService {

  static $inject = [
    CordovaService.serviceId,
    '$rootScope',
  ];
  static serviceId = 'scanner';

  constructor(private cordova: CordovaService,
              private $rootScope: ng.IRootScopeService,) {

  }

  scan(): Promise<ScanResult> {
    const config: IScannerConfig = {
      showTorchButton: true,
      preferFrontCamera: this.cordova.isTablet,
      showFlipCameraButton: !this.cordova.isTablet,
    };

    return this.scanCode(config);
  }

  async scanPosition(): Promise<{ value: number | null }> {
    const result = await this.scan();
    if (!result || result.cancelled || !result.text) {
      return {value: null};
    }

    if (result.text.startsWith('L$')) {
      return {value: parseInt(result.text.replace('L$', '')) || null};
    }

    return {value: null};
  }

  async scanProject(): Promise<{ value: number | null, type: number | null }> {
    const result = await this.scan();
    if (!result || result.cancelled) {
      return {value: null, type: null};
    }

    if (!result.text) {
      return {value: null, type: null};
    }

    if (result.text.startsWith('P$')) {
      return {value: parseInt(result.text.replace('P$', '')) || null, type: EExternalProjectType.Proj};
    }

    if (result.text.startsWith('S$')) {
      return {value: parseInt(result.text.replace('S$', '')) || null, type: EExternalProjectType.Svm};
    }

    return {value: parseInt(result.text) || null, type: null};
  }

  scanCode(config: IScannerConfig): Promise<ScanResult> {
    return this.scanCodeImpl(config).then((r) => {
      return this.$rootScope.$apply(()=> r);
    }, (e) => {
      this.$rootScope.$applyAsync();
      throw e;
    });
  }

  private async scanCodeImpl(config: IScannerConfig): Promise<ScanResult> {
    return new Promise<ScanResult>(async (resolve, reject) => {
      try {
        // Check if the device has a camera
        const {supported} = await BarcodeScanner.isSupported();

        if (!supported) {
          // Fallback if camera is not available
          const result = prompt('Enter barcode value (Camera not available)');
          if (result === null) {
            reject({text: '', format: '', cancelled: true});
            return;
          } else {
            resolve({text: result, format: 'QR_CODE', cancelled: false});
            return;
          }
        }

        // Request camera permissions
        const permission = await BarcodeScanner.checkPermissions();
        if (permission.camera === 'denied') {
          const {camera} = await BarcodeScanner.requestPermissions();

          if (camera === 'denied') {
            console.error('Camera permission denied');
            reject({text: '', format: '', cancelled: true});
            return;
          }
        }


        // Configure scan options
        let videoElement = await this.createVideoElement(config.preferFrontCamera ? CameraDirection.Front : CameraDirection.Back,
            () => reject('user-cancelled'));
        try {
          const scanOptions: StartScanOptions = {
            formats: [BarcodeFormat.QrCode],
            lensFacing: config.preferFrontCamera ? LensFacing.Front : LensFacing.Back,
            videoElement: videoElement.video,
          };

          // Start scanning
          await BarcodeScanner.startScan(scanOptions);
          await BarcodeScanner.addListener('scanError', (err) => {
            console.error('Barcode scanning error:', err);
            BarcodeScanner.removeAllListeners();
            reject(err);
          });
          await BarcodeScanner.addListener('barcodesScanned', (result) => {
            BarcodeScanner.removeAllListeners();
            videoElement.cleanup();
            BarcodeScanner.stopScan();
            // Map the result to our interface
            let barcode = result.barcodes[0];
            if (!barcode) {
              reject('No barcode found');
            } else {
              resolve({
                text: barcode.rawValue || '',
                format: barcode.format || '',
                cancelled: !result.barcodes || result.barcodes.length === 0,
              });
            }
          });
        } catch (er) {
          videoElement.cleanup();
          reject(er);
          return;
        }
      } catch (error) {
        console.error('Barcode scanning error:', error);
        BarcodeScanner.removeAllListeners();
        reject(error);
        return;
      }
    });

  }

  private createVideoElement(cameraDirection: CameraDirection, onCancel: () => void) {
    const cameraContainer = document.createElement('div');
    const video = document.createElement('video');
    const cleanup = async () => {
      try {
        await BarcodeScanner.stopScan();
      } catch (e) {
      }
      try {
        document.body.removeChild(cameraContainer);
      } catch {
      }

    };
    // Request front camera
    cameraContainer.style.position = 'fixed';
    cameraContainer.style.top = '0';
    cameraContainer.style.left = '0';
    cameraContainer.style.width = '100%';
    cameraContainer.style.height = '100%';
    cameraContainer.style.backgroundColor = 'black';
    cameraContainer.style.zIndex = '9999';


    video.style.width = '100%';
    video.style.height = '100%';
    (<any>video.style).objectFit = 'cover';
    video.autoplay = true;
    video.playsInline = true;
    cameraContainer.appendChild(video);

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Abbrechen';
    cancelButton.style.position = 'absolute';
    cancelButton.style.bottom = '20px';
    cancelButton.style.left = '20px';
    cancelButton.style.padding = '10px 20px';
    cancelButton.style.backgroundColor = 'white';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '4px';
    cancelButton.style.fontSize = '16px';
    cameraContainer.appendChild(cancelButton);
// Handle cancel button click
    cancelButton.onclick = () => {

      cleanup();
      onCancel();
    };
    document.body.appendChild(cameraContainer);
    return {cameraContainer, video, cancelButton, cleanup};
  }
}
